<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import useService from '../../../../Composables/Service/useService.ts'
import type { Service } from '../../../../Types/service'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import type FormCreator from '../UI/Form/FormCreator.vue'
import ServiceLevelFormItem from './ServiceLevelFormItem.vue'
import ServiceTermsMismatchAlert from './ServiceTermsMismatchAlert.vue'

const props = defineProps<{
  service: Service
  form: InstanceType<typeof FormCreator>
}>()

const { t } = useI18n()
const { hasMissingTerms, hasStartDate, levelTerms, totalTerms, addNewLevel, removeLevel } = useService(props.service)

const totalTermClasses = computed(() => ({
  'text-danger': hasMissingTerms.value,
  'font-weight-bold': hasMissingTerms.value,
  'total-term-value': true,
}))

watch(
  () => props.service.levels,
  (value) => {
    if (!props.form || !Array.isArray(value)) {
      return
    }

    props.form.setFormData(
      'levels',
      value.map(({ terms, amount }) => ({ terms, amount })),
    )
    props.form.setObjectValue('levels', value)
  },
  { deep: true },
)
</script>

<template>
  <table class="table w-100">
    <thead>
      <tr>
        <th>{{ t('service.fields.levels.position.label') }}</th>
        <th v-if="hasStartDate" data-test="service-level-form-header-startAt">
          {{ t('service.fields.levels.startAt.label') }}
        </th>
        <th>{{ t('service.fields.levels.amount.label') }}</th>
        <th>{{ t('service.fields.levels.terms.label') }}</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <ServiceLevelFormItem
        v-for="(level, index) in service.levels"
        :key="level.position"
        :service="service"
        :level="level"
        :form="form"
        :index="index"
        @remove="removeLevel(index)"
      />
    </tbody>
    <tfoot>
      <tr>
        <td :colspan="hasStartDate ? 3 : 2" class="text-right">
          <div class="font-weight-bold">{{ t('service.totalTerms') }}</div>
        </td>
        <td :class="totalTermClasses">{{ levelTerms }} / {{ totalTerms }}</td>
        <td>
          <ButtonAll
            :title="t('service.action.add_level')"
            type="action"
            icon="add"
            size="small"
            margin="no"
            e2e-button="addServiceLevel"
            data-test="service-level-form-add-level"
            @click="addNewLevel"
          />
        </td>
      </tr>
    </tfoot>
  </table>
  <ServiceTermsMismatchAlert v-if="hasMissingTerms" />
</template>
<style scoped>
.total-term-value {
  padding-left: 32px;
}
</style>
